/**
 * MANAGEMENT (14) · Pricing + billing screen i18n (FR default + AR-RTL). ADDITIVE to the shell
 * ../messages.ts — this registers ONLY the NEW keys the pricing table + the plan/assign/checkout modals +
 * the billing panel need, PLUS the `nav.mgmt.pricing` label (the shell's `nav.mgmt.*` band owns the sibling
 * labels; adding one more key is additive + collision-free — `registerMessages` is `Object.assign`). The
 * screen calls `registerPricingMessages()` at module-eval (a `registered` latch makes it a no-op after the
 * first). A missing AR key falls back to the FR value, then the raw key (never a blank, never a crash).
 *
 * Codes / SKUs / prices / dates render inside `.nv-ltr-island` so figures read identically under dir="rtl".
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── nav (the platform-section label for this screen) ────────────────────────────
  "nav.mgmt.pricing": "Tarification",

  // ── page header ──────────────────────────────────────────────────────────────────
  "mgmt.pricing.title": "Tarification et facturation",
  "mgmt.pricing.subtitle": "Catalogue de plans, affectation d'un plan à un laboratoire et facturation Chargily.",
  "mgmt.pricing.action.newPlan": "Nouveau plan",

  // ── tabs ───────────────────────────────────────────────────────────────────────
  "mgmt.pricing.tab.catalogue": "Catalogue",
  "mgmt.pricing.tab.billing": "Facturation",

  // ── catalogue / pricing table ─────────────────────────────────────────────────────
  "mgmt.pricing.catalogue.title": "Catalogue de plans",
  "mgmt.pricing.catalogue.subtitle": "Chaque palier définit son prix mensuel et annuel et les modules inclus.",
  "mgmt.pricing.showRetired": "Afficher les plans retirés",
  "mgmt.pricing.perMonth": "/ mois",
  "mgmt.pricing.perYear": "/ an",
  "mgmt.pricing.annualNote": "{price} facturé annuellement",
  "mgmt.pricing.noAnnual": "Pas de tarif annuel",
  "mgmt.pricing.mostPopular": "Le plus populaire",
  "mgmt.pricing.retiredBadge": "Retiré",
  "mgmt.pricing.modulesTitle": "Modules inclus",
  "mgmt.pricing.cloudTitle": "SKU cloud",
  "mgmt.pricing.noModules": "Aucun module",
  "mgmt.pricing.card.assign": "Affecter à un laboratoire",
  "mgmt.pricing.card.checkout": "Créer un paiement",
  "mgmt.pricing.card.edit": "Modifier",
  "mgmt.pricing.card.retire": "Retirer",
  "mgmt.pricing.card.restore": "Restaurer",
  "mgmt.pricing.empty.title": "Aucun plan au catalogue",
  "mgmt.pricing.empty.desc": "Publiez un premier palier pour commencer à facturer les laboratoires.",
  "mgmt.pricing.empty.action": "Publier un plan",

  // ── tier labels ───────────────────────────────────────────────────────────────
  "mgmt.pricing.tier.starter": "Starter",
  "mgmt.pricing.tier.standard": "Standard",
  "mgmt.pricing.tier.pro": "Pro",
  "mgmt.pricing.tier.enterprise": "Entreprise",

  // ── plan create/edit modal ─────────────────────────────────────────────────────
  "mgmt.pricing.plan.new.title": "Publier un plan",
  "mgmt.pricing.plan.edit.title": "Modifier le plan — {code}",
  "mgmt.pricing.plan.code": "Code",
  "mgmt.pricing.plan.code.placeholder": "ex. PRO, STARTER",
  "mgmt.pricing.plan.code.hint": "Identifiant immuable du palier (majuscules).",
  "mgmt.pricing.plan.nom": "Nom commercial",
  "mgmt.pricing.plan.description": "Description",
  "mgmt.pricing.plan.tier": "Palier",
  "mgmt.pricing.plan.prixMensuel": "Prix mensuel (DZD)",
  "mgmt.pricing.plan.prixAnnuel": "Prix annuel (DZD)",
  "mgmt.pricing.plan.prixAnnuel.hint": "Laissez vide s'il n'y a pas d'offre annuelle.",
  "mgmt.pricing.plan.modules": "Modules inclus",
  "mgmt.pricing.plan.modules.hint": "Codes de modules séparés par une virgule (ex. lis, facturation).",
  "mgmt.pricing.plan.cloudSku": "SKU cloud",
  "mgmt.pricing.plan.cloudSku.hint": "SKU cloud séparés par une virgule (suspendus hors statut actif).",
  "mgmt.pricing.plan.ordre": "Ordre d'affichage",
  "mgmt.pricing.plan.submit.create": "Publier le plan",
  "mgmt.pricing.plan.submit.edit": "Enregistrer",
  "mgmt.pricing.plan.saved.create": "Plan publié.",
  "mgmt.pricing.plan.saved.edit": "Plan mis à jour.",
  "mgmt.pricing.plan.codeRequired": "Le code est requis.",
  "mgmt.pricing.plan.nomRequired": "Le nom est requis.",
  "mgmt.pricing.plan.prixInvalid": "Le prix mensuel doit être un montant valide.",
  "mgmt.pricing.plan.prixAnnuelInvalid": "Le prix annuel doit être un montant valide ou vide.",

  // ── retire / restore confirm ─────────────────────────────────────────────────────
  "mgmt.pricing.retire.title": "Retirer le plan — {code}",
  "mgmt.pricing.retire.warn":
    "Le plan ne sera plus proposé à la sélection. Les paiements existants qui le référencent sont conservés. Il peut être restauré ultérieurement.",
  "mgmt.pricing.retire.submit": "Confirmer le retrait",
  "mgmt.pricing.retired": "Plan retiré.",
  "mgmt.pricing.restored": "Plan restauré.",

  // ── assign-plan modal ────────────────────────────────────────────────────────────
  "mgmt.pricing.assign.title": "Affecter « {plan} » à un laboratoire",
  "mgmt.pricing.assign.labId": "Identifiant du laboratoire",
  "mgmt.pricing.assign.labId.hint": "Identifiant numérique du laboratoire cible.",
  "mgmt.pricing.assign.statut": "Statut de l'abonnement",
  "mgmt.pricing.assign.reason": "Motif",
  "mgmt.pricing.assign.reason.hint": "Pourquoi cette affectation (tracé sur la projection).",
  "mgmt.pricing.assign.submit": "Affecter le plan",
  "mgmt.pricing.assign.done": "Plan affecté au laboratoire.",
  "mgmt.pricing.assign.labIdRequired": "L'identifiant du laboratoire est requis (numérique).",
  "mgmt.pricing.subStatut.ESSAI": "Essai",
  "mgmt.pricing.subStatut.ACTIVE": "Actif",
  "mgmt.pricing.subStatut.PAST_DUE": "Impayé (grâce)",
  "mgmt.pricing.subStatut.SUSPENDUE": "Suspendu",
  "mgmt.pricing.subStatut.EXPIREE": "Expiré",
  "mgmt.pricing.subStatut.ANNULEE": "Annulé",

  // ── checkout modal ───────────────────────────────────────────────────────────────
  "mgmt.pricing.checkout.title": "Créer un paiement — « {plan} »",
  "mgmt.pricing.checkout.labId": "Identifiant du laboratoire",
  "mgmt.pricing.checkout.cycle": "Cycle de facturation",
  "mgmt.pricing.checkout.cycle.mensuel": "Mensuel",
  "mgmt.pricing.checkout.cycle.annuel": "Annuel",
  "mgmt.pricing.checkout.amount": "Montant à facturer",
  "mgmt.pricing.checkout.submit": "Générer le paiement",
  "mgmt.pricing.checkout.simulatedNote":
    "Aucune clé Chargily n'est configurée : un flux de paiement SIMULÉ (mode test) est généré.",
  "mgmt.pricing.checkout.created": "Paiement créé.",
  "mgmt.pricing.checkout.urlTitle": "Lien de paiement",
  "mgmt.pricing.checkout.open": "Ouvrir le paiement Chargily",
  "mgmt.pricing.checkout.simulatedBadge": "Simulé (test)",
  "mgmt.pricing.checkout.liveBadge": "Chargily (test)",
  "mgmt.pricing.checkout.close": "Fermer",

  // ── billing / payments panel ─────────────────────────────────────────────────────
  "mgmt.pricing.billing.title": "Facturation par laboratoire",
  "mgmt.pricing.billing.subtitle": "Consultez l'historique des paiements d'un laboratoire et générez un paiement.",
  "mgmt.pricing.billing.labId": "Identifiant du laboratoire",
  "mgmt.pricing.billing.labId.placeholder": "ex. 10",
  "mgmt.pricing.billing.load": "Consulter",
  "mgmt.pricing.billing.selectPrompt.title": "Sélectionnez un laboratoire",
  "mgmt.pricing.billing.selectPrompt.desc": "Saisissez un identifiant de laboratoire pour afficher son historique de paiements.",
  "mgmt.pricing.billing.col.created": "Date",
  "mgmt.pricing.billing.col.plan": "Plan",
  "mgmt.pricing.billing.col.cycle": "Cycle",
  "mgmt.pricing.billing.col.amount": "Montant",
  "mgmt.pricing.billing.col.statut": "Statut",
  "mgmt.pricing.billing.col.checkout": "Réf. checkout",
  "mgmt.pricing.billing.empty.title": "Aucun paiement",
  "mgmt.pricing.billing.empty.desc": "Ce laboratoire n'a encore aucun paiement enregistré.",
  "mgmt.pricing.cycle.mensuel": "Mensuel",
  "mgmt.pricing.cycle.annuel": "Annuel",
  "mgmt.pricing.payStatut.en_attente": "En attente",
  "mgmt.pricing.payStatut.paye": "Payé",
  "mgmt.pricing.payStatut.echec": "Échec",

  // ── generic ────────────────────────────────────────────────────────────────────
  "mgmt.pricing.cancel": "Annuler",
  "mgmt.pricing.error": "Une erreur est survenue.",
};

const AR: Record<string, string> = {
  "nav.mgmt.pricing": "التسعير",

  "mgmt.pricing.title": "التسعير والفوترة",
  "mgmt.pricing.subtitle": "كتالوج الخطط، تعيين خطة لمختبر، والفوترة عبر Chargily.",
  "mgmt.pricing.action.newPlan": "خطة جديدة",

  "mgmt.pricing.tab.catalogue": "الكتالوج",
  "mgmt.pricing.tab.billing": "الفوترة",

  "mgmt.pricing.catalogue.title": "كتالوج الخطط",
  "mgmt.pricing.catalogue.subtitle": "كل مستوى يحدّد سعره الشهري والسنوي والوحدات المضمّنة.",
  "mgmt.pricing.showRetired": "إظهار الخطط المسحوبة",
  "mgmt.pricing.perMonth": "/ شهريًا",
  "mgmt.pricing.perYear": "/ سنويًا",
  "mgmt.pricing.annualNote": "{price} تُفوتر سنويًا",
  "mgmt.pricing.noAnnual": "لا يوجد سعر سنوي",
  "mgmt.pricing.mostPopular": "الأكثر شيوعًا",
  "mgmt.pricing.retiredBadge": "مسحوب",
  "mgmt.pricing.modulesTitle": "الوحدات المضمّنة",
  "mgmt.pricing.cloudTitle": "وحدات SKU السحابية",
  "mgmt.pricing.noModules": "لا توجد وحدات",
  "mgmt.pricing.card.assign": "تعيين لمختبر",
  "mgmt.pricing.card.checkout": "إنشاء دفعة",
  "mgmt.pricing.card.edit": "تعديل",
  "mgmt.pricing.card.retire": "سحب",
  "mgmt.pricing.card.restore": "استعادة",
  "mgmt.pricing.empty.title": "لا توجد خطط في الكتالوج",
  "mgmt.pricing.empty.desc": "انشر أول مستوى لبدء فوترة المختبرات.",
  "mgmt.pricing.empty.action": "نشر خطة",

  "mgmt.pricing.tier.starter": "مبتدئ",
  "mgmt.pricing.tier.standard": "قياسي",
  "mgmt.pricing.tier.pro": "احترافي",
  "mgmt.pricing.tier.enterprise": "مؤسسة",

  "mgmt.pricing.plan.new.title": "نشر خطة",
  "mgmt.pricing.plan.edit.title": "تعديل الخطة — {code}",
  "mgmt.pricing.plan.code": "الرمز",
  "mgmt.pricing.plan.code.placeholder": "مثال: PRO، STARTER",
  "mgmt.pricing.plan.code.hint": "معرّف ثابت للمستوى (بأحرف كبيرة).",
  "mgmt.pricing.plan.nom": "الاسم التجاري",
  "mgmt.pricing.plan.description": "الوصف",
  "mgmt.pricing.plan.tier": "المستوى",
  "mgmt.pricing.plan.prixMensuel": "السعر الشهري (دج)",
  "mgmt.pricing.plan.prixAnnuel": "السعر السنوي (دج)",
  "mgmt.pricing.plan.prixAnnuel.hint": "اتركه فارغًا إن لم يكن هناك عرض سنوي.",
  "mgmt.pricing.plan.modules": "الوحدات المضمّنة",
  "mgmt.pricing.plan.modules.hint": "رموز الوحدات مفصولة بفواصل (مثال: lis، facturation).",
  "mgmt.pricing.plan.cloudSku": "وحدات SKU السحابية",
  "mgmt.pricing.plan.cloudSku.hint": "وحدات SKU سحابية مفصولة بفواصل (تُعلَّق خارج الحالة النشطة).",
  "mgmt.pricing.plan.ordre": "ترتيب العرض",
  "mgmt.pricing.plan.submit.create": "نشر الخطة",
  "mgmt.pricing.plan.submit.edit": "حفظ",
  "mgmt.pricing.plan.saved.create": "تم نشر الخطة.",
  "mgmt.pricing.plan.saved.edit": "تم تحديث الخطة.",
  "mgmt.pricing.plan.codeRequired": "الرمز مطلوب.",
  "mgmt.pricing.plan.nomRequired": "الاسم مطلوب.",
  "mgmt.pricing.plan.prixInvalid": "يجب أن يكون السعر الشهري مبلغًا صالحًا.",
  "mgmt.pricing.plan.prixAnnuelInvalid": "يجب أن يكون السعر السنوي مبلغًا صالحًا أو فارغًا.",

  "mgmt.pricing.retire.title": "سحب الخطة — {code}",
  "mgmt.pricing.retire.warn":
    "لن تُعرَض الخطة للاختيار بعد الآن. تُحفَظ الدفعات القائمة التي تشير إليها. يمكن استعادتها لاحقًا.",
  "mgmt.pricing.retire.submit": "تأكيد السحب",
  "mgmt.pricing.retired": "تم سحب الخطة.",
  "mgmt.pricing.restored": "تمت استعادة الخطة.",

  "mgmt.pricing.assign.title": "تعيين « {plan} » لمختبر",
  "mgmt.pricing.assign.labId": "معرّف المختبر",
  "mgmt.pricing.assign.labId.hint": "المعرّف الرقمي للمختبر المستهدف.",
  "mgmt.pricing.assign.statut": "حالة الاشتراك",
  "mgmt.pricing.assign.reason": "السبب",
  "mgmt.pricing.assign.reason.hint": "سبب هذا التعيين (يُسجَّل على الإسقاط).",
  "mgmt.pricing.assign.submit": "تعيين الخطة",
  "mgmt.pricing.assign.done": "تم تعيين الخطة للمختبر.",
  "mgmt.pricing.assign.labIdRequired": "معرّف المختبر مطلوب (رقمي).",
  "mgmt.pricing.subStatut.ESSAI": "تجريبي",
  "mgmt.pricing.subStatut.ACTIVE": "نشط",
  "mgmt.pricing.subStatut.PAST_DUE": "متأخر الدفع (سماح)",
  "mgmt.pricing.subStatut.SUSPENDUE": "موقوف",
  "mgmt.pricing.subStatut.EXPIREE": "منتهٍ",
  "mgmt.pricing.subStatut.ANNULEE": "ملغى",

  "mgmt.pricing.checkout.title": "إنشاء دفعة — « {plan} »",
  "mgmt.pricing.checkout.labId": "معرّف المختبر",
  "mgmt.pricing.checkout.cycle": "دورة الفوترة",
  "mgmt.pricing.checkout.cycle.mensuel": "شهري",
  "mgmt.pricing.checkout.cycle.annuel": "سنوي",
  "mgmt.pricing.checkout.amount": "المبلغ المطلوب",
  "mgmt.pricing.checkout.submit": "توليد الدفعة",
  "mgmt.pricing.checkout.simulatedNote":
    "لا يوجد مفتاح Chargily مُهيَّأ: يتم توليد تدفّق دفع محاكى (وضع الاختبار).",
  "mgmt.pricing.checkout.created": "تم إنشاء الدفعة.",
  "mgmt.pricing.checkout.urlTitle": "رابط الدفع",
  "mgmt.pricing.checkout.open": "فتح دفعة Chargily",
  "mgmt.pricing.checkout.simulatedBadge": "محاكى (اختبار)",
  "mgmt.pricing.checkout.liveBadge": "Chargily (اختبار)",
  "mgmt.pricing.checkout.close": "إغلاق",

  "mgmt.pricing.billing.title": "الفوترة حسب المختبر",
  "mgmt.pricing.billing.subtitle": "اطّلع على سجل دفعات مختبر وولّد دفعة.",
  "mgmt.pricing.billing.labId": "معرّف المختبر",
  "mgmt.pricing.billing.labId.placeholder": "مثال: 10",
  "mgmt.pricing.billing.load": "عرض",
  "mgmt.pricing.billing.selectPrompt.title": "اختر مختبرًا",
  "mgmt.pricing.billing.selectPrompt.desc": "أدخل معرّف مختبر لعرض سجل دفعاته.",
  "mgmt.pricing.billing.col.created": "التاريخ",
  "mgmt.pricing.billing.col.plan": "الخطة",
  "mgmt.pricing.billing.col.cycle": "الدورة",
  "mgmt.pricing.billing.col.amount": "المبلغ",
  "mgmt.pricing.billing.col.statut": "الحالة",
  "mgmt.pricing.billing.col.checkout": "مرجع الدفع",
  "mgmt.pricing.billing.empty.title": "لا توجد دفعات",
  "mgmt.pricing.billing.empty.desc": "لا توجد أي دفعة مسجّلة لهذا المختبر بعد.",
  "mgmt.pricing.cycle.mensuel": "شهري",
  "mgmt.pricing.cycle.annuel": "سنوي",
  "mgmt.pricing.payStatut.en_attente": "قيد الانتظار",
  "mgmt.pricing.payStatut.paye": "مدفوع",
  "mgmt.pricing.payStatut.echec": "فشل",

  "mgmt.pricing.cancel": "إلغاء",
  "mgmt.pricing.error": "حدث خطأ.",
};

let registered = false;
export function registerPricingMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
